import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bot, CheckCircle, ChevronLeft, FileText, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../App';
import { toast } from 'sonner';

export default function Phase1AIInterview({ user }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const start = async () => {
      try {
        const res = await api.post(`/ai-interviews/start/${jobId}`);
        setSession(res.data);
        const saved = {};
        (res.data.answers || []).forEach((answer) => {
          saved[answer.question_id] = answer.answer;
        });
        setAnswers(saved);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to start Phase 1 AI Interview');
        navigate('/candidate/dashboard');
      }
    };
    if (jobId) start();
  }, [jobId, navigate]);

  const answeredCount = useMemo(() => (
    Object.values(answers).filter((answer) => answer.trim().length >= 20).length
  ), [answers]);

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const submitInterview = async () => {
    if (!session) return;
    const payload = {
      session_id: session.id,
      answers: session.questions.map((question) => ({
        question_id: question.id,
        answer: answers[question.id] || '',
      })).filter((answer) => answer.answer.trim()),
    };

    setSubmitting(true);
    try {
      const res = await api.post('/ai-interviews/submit', payload);
      setSession(res.data);
      toast.success('Phase 1 AI Interview submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit interview');
    } finally {
      setSubmitting(false);
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="mx-auto max-w-3xl border bg-white p-8">Loading Phase 1 AI Interview...</div>
      </div>
    );
  }

  const report = session.report;
  const completed = Boolean(report);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white">
        <div className="container mx-auto flex items-center justify-between px-6 py-4">
          <button onClick={() => navigate('/candidate/dashboard')} className="flex items-center gap-2 text-sm">
            <ChevronLeft size={16} /> Back to jobs
          </button>
          <div className="text-sm text-muted-foreground">{user?.full_name}</div>
        </div>
      </header>

      <main className="container mx-auto grid gap-6 px-6 py-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="space-y-5">
          <div className="border bg-white p-6">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-slate-900 text-white">
                <Bot size={24} />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">Phase 1 AI Interview</p>
                <h1 className="text-2xl font-heading font-bold">{session.job?.title}</h1>
                <p className="text-sm text-muted-foreground">{session.company_name}</p>
              </div>
            </div>
            <p className="text-slate-700">
              I am your Phase 1 interview guide. Answer naturally, use examples, and show what you personally did.
              This report helps the company prepare a better Phase 2 human interview.
            </p>
          </div>

          <div className="border bg-blue-50 p-5 text-sm text-blue-950">
            <div className="mb-2 flex items-center gap-2 font-semibold">
              <ShieldCheck size={18} /> Candidate notice and fairness guardrails
            </div>
            <p>{session.notice?.summary}</p>
            <p className="mt-2">{session.notice?.alternative_process}</p>
            <p className="mt-2">{session.notice?.data_used}</p>
          </div>

          {completed ? (
            <div className="border bg-white p-6" data-testid="ai-interview-report">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-heading font-semibold">Your Phase 1 Report</h2>
                  <p className="text-sm text-muted-foreground">Use this to prepare before the human interview.</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{report.score}</div>
                  <div className="text-xs text-muted-foreground">threshold {report.threshold}</div>
                  <div className="mt-1 text-xs text-blue-700">
                    {report.evaluator?.provider === 'openai' ? 'OpenAI structured evaluation' : 'Secure backup evaluation'}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="border p-4">
                  <h3 className="font-semibold">Strengths</h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                    {report.strengths.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
                <div className="border p-4">
                  <h3 className="font-semibold">Improve Before Phase 2</h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                    {report.gaps.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>

              <div className="mt-4 border p-4">
                <h3 className="font-semibold">Candidate Success Plan</h3>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                  {report.candidate_success_plan.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <Button onClick={() => navigate('/candidate/dashboard')}>
                  <CheckCircle size={16} className="mr-2" /> Return to book Phase 2
                </Button>
                <Button variant="outline" onClick={() => setSession({ ...session, report: null, status: 'started' })}>
                  Practice answers again
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {session.questions.map((question, index) => (
                <div key={question.id} className="border bg-white p-5" data-testid={`ai-question-${question.id}`}>
                  <div className="mb-3 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Question {index + 1}</p>
                      <h2 className="font-semibold">{question.prompt}</h2>
                    </div>
                    <span className="shrink-0 rounded border bg-slate-50 px-2 py-1 text-xs text-slate-600">{question.type}</span>
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">{question.rubric}</p>
                  <Textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => updateAnswer(question.id, e.target.value)}
                    rows={5}
                    placeholder="Answer with a real example. Include what happened, what you did, and the result."
                    data-testid={`ai-answer-${question.id}`}
                  />
                </div>
              ))}
              <p className="text-center text-sm text-muted-foreground">
                {answeredCount}/{session.questions.length} complete. Every question requires a meaningful response.
              </p>
              <Button className="w-full" onClick={submitInterview} disabled={submitting || answeredCount < session.questions.length} data-testid="submit-ai-interview">
                {submitting ? 'Scoring...' : answeredCount < session.questions.length ? 'Complete Every Question to Submit' : 'Submit Phase 1 AI Interview'}
              </Button>
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <div className="border bg-white p-5">
            <div className="mb-2 flex items-center gap-2 font-semibold">
              <Sparkles size={18} className="text-purple-600" /> How to succeed
            </div>
            <ul className="list-disc space-y-2 pl-5 text-sm text-slate-700">
              <li>Use STAR: Situation, Task, Action, Result.</li>
              <li>Talk about what you personally owned.</li>
              <li>Mention skills from the job listing when they apply.</li>
              <li>Be honest about support or training you need.</li>
            </ul>
          </div>

          <div className="border bg-white p-5">
            <div className="mb-2 flex items-center gap-2 font-semibold">
              <FileText size={18} className="text-blue-600" /> Phase 2 handoff
            </div>
            <p className="text-sm text-slate-700">
              The company receives your transcript, score breakdown, strengths, gaps, and recommended human follow-up questions.
            </p>
          </div>
        </aside>
      </main>
    </div>
  );
}
